import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { LineageExplorerDialogsObject } from '@databox/ember-integrations/lib/components/Datasets/LineageExplorer/LineageExplorer.types';
import LineageExplorerEntryPoint from '@databox/ember-integrations/lib/components/Datasets/LineageExplorer/LineageExplorerEntryPoint';

import type {
	LineageNavigateIntent,
	LineageVerifyTarget,
	ReactIntegrations,
	Service,
} from 'webapp-types';
import { IntercomCover } from 'webapp/enums/intercom';
import { getShadowDomMode } from 'webapp/utils/common';
import { mountReactRoot, unmountReactRoot } from 'webapp/utils/react-mount';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';

export type OpenLineageExplorer = LineageExplorerDialogsObject['openLineageExplorer'];

interface LineageExplorerHostSignature {
	Element: HTMLDivElement;
	Args: {
		onReady: (open: OpenLineageExplorer) => void;
		onDestroy?: (open: OpenLineageExplorer) => void;
	};
}

export default class LineageExplorerHostComponent extends Component<LineageExplorerHostSignature> {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('react') declare reactService: Service.React;
	@service('dataset-events') declare datasetEventsService: Service.DatasetEvents;
	@service('intercom') declare intercomService: Service.Intercom;

	reactElement: ReactIntegrations.Root = null;
	private registeredOpen: OpenLineageExplorer | null = null;

	@action
	setupView(node: HTMLElement) {
		mountReactRoot(this, node, () => this.renderBridge());
	}

	@action
	teardownView() {
		if (this.registeredOpen) {
			this.args.onDestroy?.(this.registeredOpen);
			this.registeredOpen = null;
		}
		// leaving the page with the explorer open unmounts it without `onClose`
		this.intercomService.uncover(IntercomCover.LINEAGE_EXPLORER);
		unmountReactRoot(this);
	}

	private renderBridge() {
		const props = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			onNavigate: (kind: string, id: string, intent?: LineageNavigateIntent) =>
				this.datasetEventsService.handleLineageNavigate(kind, id, intent),
			onVerify: (target: LineageVerifyTarget) =>
				this.datasetEventsService.handleLineageVerify(target),
			verifiableKinds: ['Dataset', 'Metric'],
			onClose: () => this.intercomService.uncover(IntercomCover.LINEAGE_EXPLORER),
			onInit: (dialogs: LineageExplorerDialogsObject) => {
				const open: OpenLineageExplorer = (args) => {
					this.intercomService.cover(IntercomCover.LINEAGE_EXPLORER);
					dialogs.openLineageExplorer(args);
				};
				this.registeredOpen = open;
				this.args.onReady(open);
			},
		};

		if (this.reactElement) {
			this.reactService.render(
				this.reactElement,
				LineageExplorerEntryPoint(props),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	<template>
		<div
			class="lineage-explorer-host"
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		></div>
	</template>
}
